import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import { ColumnDef } from '@tanstack/react-table';
import lodashGet from 'lodash.get';
import { getCellWithMask } from './cell-masks';
import Link from 'next/link';
import { TableMaskType } from '../core/table-creator';

/**
 * Determines appropriate alignment based on column mask type
 */
const getApplyAlign = (
  mask: TableMaskType,
  defaultAlign: string
): null | string => {
  if (!mask || defaultAlign) return defaultAlign;

  switch (mask) {
    case TableMaskType.INTEGER:
    case TableMaskType.DECIMAL:
    case TableMaskType.MONEY:
    case TableMaskType.PERCENT:
    case TableMaskType.PERCENT_OTHER:
      return 'right';
    default:
      return defaultAlign;
  }
};

/**
 * Aplica o trim em um valor string de acordo com o comprimento especificado
 */
const applyTrim = (value: any, length: number = 30): string => {
  if (typeof value !== 'string' || !length || value.length <= length) {
    return value;
  }
  return `${value.substring(0, length)}...`;
};

/**
 * Creates selection column for tables with row selection
 */
export const createSelectionColumn = (checkboxSize?: number): ColumnDef<any> => {
  return {
    id: 'select-col',
    accessorKey: 'id',
    size: checkboxSize || 48,
    minSize: checkboxSize || 48,
    maxSize: checkboxSize || 48,
    header: ({ table }: any) => (
      <Checkbox
        sx={{ p: 0 }}
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
      />
    ),
    cell: ({ row }: any) => (
      <Checkbox
        sx={{ p: 0 }}
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onChange={row.getToggleSelectedHandler()}
      />
    ),
  };
};

/**
 * Creates formatted columns with proper cell rendering
 */
export const createFormattedColumns = (columns: ColumnDef<any, any>[]) => {
  return columns.map((column) => {
    let meta = (column.meta || {}) as any;

    return {
      ...column,
      meta: {
        ...meta,
        align: getApplyAlign(meta?.mask, meta?.align),
      },
      cell:
        column.cell ||
        ((info: any) => {
          const value = info.getValue();
          const currency =
            meta?.currency || info.row.original.currency || 'BRL';

          let cellValue = getCellWithMask({
            value,
            meta,
            mask: meta?.mask,
            currency,
            info,
          });

          // Aplica o trim se configurado
          if (meta?.trim) {
            cellValue = applyTrim(cellValue, meta.trim);
          }

          const isLink = meta?.isLink;

          // Cria o conteúdo da célula com a barra indicadora se configurada
          let content = cellValue;
          if (meta?.indicatorBar) {
            const color = info.row.original[meta.indicatorColorKey || 'color'];
            content = (
              <Stack direction="row" gap={1} alignItems="center" height="100%">
                <Box
                  width="4px"
                  minHeight="24px"
                  bgcolor={color || '#ccc'}
                  borderRadius={1}
                />
                {cellValue}
              </Stack>
            );
          }

          if (isLink) {
            const link = info?.row?.original?.link;
            const canonical_url = info?.row?.original?.canonical_url;
            const href = canonical_url
              ? `https://maisretorno.com/${canonical_url}`
              : link;
            const target = (meta as any)?.target;

            if (href) {
              content = (
                <Link
                  className="item_table_link"
                  href={href}
                  target={target}
                  style={{ color: '#0a1b23' }}
                  prefetch={false}
                >
                  {content}
                </Link>
              );
            }
          }

          if (!meta?.subLine) return content;

          const SubLineRender: any = meta.subLineRender;
          const subLineValue = lodashGet(info.row.original, meta.subLine);
          let subLineContent = getCellWithMask({
            value: subLineValue,
            meta,
            mask: meta?.subLineMask,
            currency,
            info,
          });

          // Aplica o trim também na sublinha se configurado
          if (meta?.trim) {
            subLineContent = applyTrim(subLineContent, meta.trim);
          }

          return (
            <Stack>
              <span>{content}</span>
              <span
                style={{
                  fontSize: '0.7rem',
                  color: '#6b7189',
                  fontWeight: 400,
                  lineHeight: 1.5,
                }}
              >
                {SubLineRender && <SubLineRender value={subLineValue} />}
                {!SubLineRender && !!subLineContent && subLineContent}
              </span>
            </Stack>
          );
        }),
    };
  });
};

/**
 * Calculates column pinning settings based on column metadata
 */
export const calculateColumnPinning = (columns: ColumnDef<any, any>[]) => {
  const left: string[] = [];
  const right: string[] = [];

  columns.forEach((column: any) => {
    const key = column.accessorKey as string;
    const pinned = (column.meta as any)?.pinned;

    if (pinned === 'left') left.push(key);
    if (pinned === 'right') right.push(key);
  });

  return { left, right };
};
